import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import Navbar from '../Components/LandingComponent/Navbar';
import SideDrawer from '../Components/newcomponents/SideDrawer';
import { collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../Components/auth/firebaseConfig';
import { fetchUserDetails } from '../DatabaseApis/DBQuery';
import { getCookie } from '../utils/cookieUtils';

const CONDITION_FIELDS = [
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'assignee', label: 'Assignee Name' },
  { value: 'assigneeEmail', label: 'Assignee Email' }
];
const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'within_days', label: 'Within Days' }
];
const ACTIONS = [
  { value: 'send_email', label: 'Send Email' },
  { value: 'show_only_matching', label: 'Show Only Matching Cards' },
  { value: 'move_to_withdrawn', label: 'Move Card to Withdrawn' },
  { value: 'set_card_highlight', label: 'Set Card Highlight' }
];

const HIGHLIGHT_COLORS = [
  { value: 'red', label: 'Red' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'green', label: 'Green' },
  { value: 'blue', label: 'Blue' },
  { value: 'orange', label: 'Orange' },
  { value: 'purple', label: 'Purple' }
];

const RulesPage = () => {
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ruleName, setRuleName] = useState('');
  const [ruleBlocks, setRuleBlocks] = useState([
    { conditions: [], actions: [] }
  ]);
  const [rules, setRules] = useState([]);
  const [expandedRuleId, setExpandedRuleId] = useState(null);
  const [editRuleId, setEditRuleId] = useState(null);
  const [newConditions, setNewConditions] = useState([{ field: '', operator: '', value: '' }]);
  const [newActions, setNewActions] = useState(['']);
  const [moveCardTargets, setMoveCardTargets] = useState([]);
  const [columns, setColumns] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [highlightColors, setHighlightColors] = useState(['red']);

  const userData = getCookie('userData');

  useEffect(() => {
    const getUserDetails = async () => {
      try {
        if (!userData?.token) {
          console.error('No user data found in session storage');
          return;
        }

        const data = await fetchUserDetails(userData.token);
        setUserRole(data.role || '');
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    };

    getUserDetails();
  }, []);

  const canManageRules = userRole === 'Admin' || userRole === 'Tenant Admin' || userRole === 'SuperAdmin';

  // Function to reset all form states
  const resetFormStates = () => {
    setRuleName('');
    setRuleBlocks([{ conditions: [], actions: [] }]);
    setNewConditions([{ field: '', operator: '', value: '' }]);
    setNewActions(['']);
    setError('');
  };

  // Function to handle opening the dialog
  const handleOpenDialog = () => {
    resetFormStates();
    setDialogOpen(true);
  };

  // Function to handle closing the dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditRuleId(null);
    resetFormStates();
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'rules'), (snapshot) => {
      const fetchedRules = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRules(fetchedRules);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setNewConditions((prev) => {
      if (ruleBlocks.length > prev.length) {
        return [...prev, { field: '', operator: '', value: '' }];
      } else if (ruleBlocks.length < prev.length) {
        return prev.slice(0, ruleBlocks.length);
      }
      return prev;
    });
    setNewActions((prev) => {
      if (ruleBlocks.length > prev.length) {
        return [...prev, ''];
      } else if (ruleBlocks.length < prev.length) {
        return prev.slice(0, ruleBlocks.length);
      }
      return prev;
    });
    setHighlightColors((prev) => {
      if (ruleBlocks.length > prev.length) {
        return [...prev, 'red'];
      } else if (ruleBlocks.length < prev.length) {
        return prev.slice(0, ruleBlocks.length);
      }
      return prev;
    });
  }, [ruleBlocks.length]);

  const handleAddBlock = () => {
    const maxBlocks = parseInt(process.env.REACT_APP_MAX_RULE_BLOCKS) || 5;
    if (ruleBlocks.length >= maxBlocks) {
      setError(`Maximum ${maxBlocks} blocks allowed per rule`);
      return;
    }
    setRuleBlocks([...ruleBlocks, { conditions: [], actions: [] }]);
  };

  const handleRemoveBlock = (blockIdx) => {
    setRuleBlocks(ruleBlocks.filter((_, i) => i !== blockIdx));
  };

  const handleAddCondition = (blockIdx, condition) => {
    setRuleBlocks(ruleBlocks.map((block, i) =>
      i === blockIdx ? { ...block, conditions: [...block.conditions, condition] } : block
    ));
  };

  const handleRemoveCondition = (blockIdx, condIdx) => {
    setRuleBlocks(ruleBlocks.map((block, i) =>
      i === blockIdx ? { ...block, conditions: block.conditions.filter((_, j) => j !== condIdx) } : block
    ));
    // Reset the new condition fields for this block
    setNewConditions(prev => prev.map((cond, i) => 
      i === blockIdx ? { field: '', operator: '', value: '' } : cond
    ));
  };

  const handleAddAction = (blockIdx, action) => {
    if (ruleBlocks[blockIdx].actions.includes(action)) {
      setError('This action is already added to this block.');
      return;
    }
    setRuleBlocks(ruleBlocks.map((block, i) =>
      i === blockIdx ? { ...block, actions: [...block.actions, action] } : block
    ));
  };

  const handleRemoveAction = (blockIdx, actIdx) => {
    setRuleBlocks(ruleBlocks.map((block, i) =>
      i === blockIdx ? { ...block, actions: block.actions.filter((_, j) => j !== actIdx) } : block
    ));
    // Reset the new action field for this block
    setNewActions(prev => prev.map((action, i) => 
      i === blockIdx ? '' : action
    ));
  };

  const handleEditRule = (rule) => {
    setEditRuleId(rule.id);
    setRuleName(rule.name);
    setRuleBlocks(rule.blocks || [{ conditions: [], actions: [] }]);
    setNewConditions([{ field: '', operator: '', value: '' }]);
    setNewActions(['']);
    setDialogOpen(true);
  };

  const handleSaveRule = async () => {
    try {
      if (!ruleName || ruleBlocks.some(block => block.conditions.length === 0 || block.actions.length === 0)) {
        setError('Each block must have at least one condition and one action.');
        return;
      }

      // Validate email notification rules
      const hasEmailNotification = ruleBlocks.some(block => 
        block.actions.includes('send_email')
      );

      if (hasEmailNotification) {
        const hasValidDueDateCondition = ruleBlocks.some(block =>
          block.conditions.some(condition =>
            condition.field === 'dueDate' && 
            condition.operator === 'within_days' &&
            !isNaN(parseInt(condition.value))
          )
        );

        if (!hasValidDueDateCondition) {
          setError('Email notification rules must include a "Within Days" condition for the due date field.');
          return;
        }
      }

      const isDuplicate = rules.some(rule =>
        (rule.name.trim().toLowerCase() === ruleName.trim().toLowerCase() ||
        JSON.stringify(rule.blocks) === JSON.stringify(ruleBlocks)) &&
        rule.id !== editRuleId
      );

      if (isDuplicate) {
        setError('A rule with the same name or identical blocks already exists.');
        return;
      }

      if (editRuleId) {
        await updateDoc(doc(db, 'rules', editRuleId), {
          name: ruleName,
          blocks: ruleBlocks
        });
      } else {
        await addDoc(collection(db, 'rules'), {
          name: ruleName,
          isActive: true,
          blocks: ruleBlocks,
          createdAt: new Date().toISOString()
        });
      }

      handleCloseDialog();
    } catch (error) {
      setError('Failed to save rule');
      console.error(error);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await deleteDoc(doc(db, 'rules', ruleId));
    } catch (error) {
      setError('Failed to delete rule');
      console.error(error);
    }
  };

  const handleToggleActive = async (ruleId, currentValue) => {
    try {
      await updateDoc(doc(db, 'rules', ruleId), { isActive: !currentValue });
    } catch (error) {
      setError('Failed to update rule status');
      console.error(error);
    }
  };

  const handleNewConditionChange = (blockIdx, key, value) => {
    setNewConditions((prev) => prev.map((cond, i) => i === blockIdx ? { ...cond, [key]: value } : cond));
  };
  const handleAddConditionClick = (blockIdx) => {
    const cond = newConditions[blockIdx];
    if (!cond.field || !cond.operator || !cond.value) return;
    handleAddCondition(blockIdx, cond);
    setNewConditions((prev) => prev.map((c, i) => i === blockIdx ? { field: '', operator: '', value: '' } : c));
  };
  const handleNewActionChange = (blockIdx, value) => {
    setNewActions((prev) => prev.map((a, i) => i === blockIdx ? value : a));
  };
  const handleHighlightColorChange = (blockIdx, color) => {
    setHighlightColors((prev) => prev.map((c, i) => i === blockIdx ? color : c));
  };
  const handleAddActionClick = (blockIdx) => {
    const actionValue = newActions[blockIdx];
    if (!actionValue) return;
    if (actionValue === 'move_to_withdrawn') {
      handleAddAction(blockIdx, { value: actionValue, targetColumn: 'withdrawn' });
    } else if (actionValue === 'set_card_highlight') {
      handleAddAction(blockIdx, { value: actionValue, color: highlightColors[blockIdx] });
    } else {
      handleAddAction(blockIdx, { value: actionValue });
    }
    setNewActions((prev) => prev.map((a, i) => i === blockIdx ? '' : a));
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <SideDrawer />
      <Navbar />
      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Rules Management</Typography>
          {canManageRules && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Create New Rule
            </Button>
          )}
        </Box>

        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{editRuleId ? 'Edit Rule' : 'Create New Rule'}</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}
            <TextField
              fullWidth
              label="Rule Name"
              value={ruleName}
              onChange={e => setRuleName(e.target.value)}
              margin="normal"
            />
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Rules for Card
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (Maximum {parseInt(process.env.REACT_APP_MAX_RULE_BLOCKS) || 5} blocks)
              </Typography>
            </Typography>
            {ruleBlocks.map((block, blockIdx) => (
              <div key={blockIdx}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ flex: 1 }}>Block {blockIdx + 1}</Typography>
                  {ruleBlocks.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => handleRemoveBlock(blockIdx)} title="Remove Block">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Field</InputLabel>
                    <Select
                      value={newConditions[blockIdx]?.field || ''}
                      onChange={(e) => handleNewConditionChange(blockIdx, 'field', e.target.value)}
                      label="Field"
                    >
                      {CONDITION_FIELDS.map(f => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={newConditions[blockIdx]?.operator || ''}
                      onChange={(e) => handleNewConditionChange(blockIdx, 'operator', e.target.value)}
                      label="Operator"
                    >
                      {OPERATORS.map(o => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {newConditions[blockIdx]?.field === 'dueDate' && newConditions[blockIdx]?.operator !== 'within_days' ? (
                    <TextField
                      sx={{ flex: 1 }}
                      label="Value"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={newConditions[blockIdx]?.value || ''}
                      onChange={(e) => handleNewConditionChange(blockIdx, 'value', e.target.value)}
                    />
                  ) : (
                    <TextField
                      sx={{ flex: 1 }}
                      label="Value"
                      value={newConditions[blockIdx]?.value || ''}
                      onChange={(e) => handleNewConditionChange(blockIdx, 'value', e.target.value)}
                      type={newConditions[blockIdx]?.operator === 'within_days' ? 'number' : 'text'}
                    />
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => handleAddConditionClick(blockIdx)}
                    disabled={!(newConditions[blockIdx]?.field && newConditions[blockIdx]?.operator && newConditions[blockIdx]?.value)}
                  >
                    Add Condition
                  </Button>
                </Box>
                <Box sx={{ mb: 2 }}>
                  {block.conditions.map((cond, condIdx) => (
                    <Paper key={`${blockIdx}-${condIdx}`} sx={{ display: 'inline-flex', alignItems: 'center', p: 1, m: 0.5 }}>
                      {cond.field} {cond.operator} {cond.value}
                      <IconButton size="small" onClick={() => handleRemoveCondition(blockIdx, condIdx)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))}
                </Box>
                <Typography variant="subtitle1">Actions</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={newActions[blockIdx] || ''}
                      onChange={(e) => handleNewActionChange(blockIdx, e.target.value)}
                      label="Action"
                    >
                      {ACTIONS.map(a => (
                        <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {/* Render Target Column dropdown only for generic move_card action */}
                  {newActions[blockIdx] === 'move_card' && (
                    <FormControl sx={{ flex: 1 }}>
                      <InputLabel>Target Column</InputLabel>
                      <Select
                        value={moveCardTargets[blockIdx] || ''}
                        onChange={(e) => {
                          setMoveCardTargets((prev) => {
                            const updated = [...prev];
                            updated[blockIdx] = e.target.value;
                            return updated;
                          });
                        }}
                        label="Target Column"
                      >
                        {columns.map(col => (
                          <MenuItem key={col.id} value={col.name}>{col.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {newActions[blockIdx] === 'set_card_highlight' && (
                    <FormControl sx={{ flex: 1 }}>
                      <InputLabel>Highlight Color</InputLabel>
                      <Select
                        value={highlightColors[blockIdx] || 'red'}
                        onChange={e => handleHighlightColorChange(blockIdx, e.target.value)}
                        label="Highlight Color"
                      >
                        {HIGHLIGHT_COLORS.map(c => (
                          <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => handleAddActionClick(blockIdx)}
                    disabled={!newActions[blockIdx]}
                  >
                    Add Action
                  </Button>
                </Box>
                <Box sx={{ mb: 2 }}>
                  {block.actions.map((act, actIdx) => (
                    <Paper key={`${blockIdx}-${actIdx}`} sx={{ display: 'inline-flex', alignItems: 'center', p: 1, m: 0.5 }}>
                      {act.value === 'set_card_highlight' && act.color
                        ? `set_card_highlight - ${act.color.charAt(0).toUpperCase() + act.color.slice(1)}`
                        : act.value}
                      <IconButton size="small" onClick={() => handleRemoveAction(blockIdx, actIdx)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))}
                </Box>
                {blockIdx < ruleBlocks.length - 1 && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    my: 2,
                    px: 2,
                    py: 1,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      OR
                    </Typography>
                  </Box>
                )}
              </div>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddBlock}
              sx={{ mt: 2 }}
            >
              Add Block
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSaveRule}>Save</Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Rules</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rule Name</TableCell>
                  <TableCell>IsActive</TableCell>
                  {canManageRules && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <React.Fragment key={rule.id}>
                    <TableRow>
                      <TableCell>
                        {rule.name}
                        <IconButton
                          onClick={() =>
                            setExpandedRuleId(expandedRuleId === rule.id ? null : rule.id)
                          }
                          title="View Details"
                          size="small"
                          sx={{ ml: 1, verticalAlign: 'middle' }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {canManageRules ? (
                          <Switch
                            checked={!!rule.isActive}
                            onChange={() => handleToggleActive(rule.id, rule.isActive)}
                            color="primary"
                            inputProps={{ 'aria-label': 'activate rule' }}
                          />
                        ) : (
                          rule.isActive ? 'Active' : 'Inactive'
                        )}
                      </TableCell>
                      {canManageRules && (
                        <TableCell>
                          <IconButton
                            onClick={() => handleEditRule(rule)}
                            title="Edit Rule"
                            size="small"
                            sx={{ ml: 1, verticalAlign: 'middle' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteRule(rule.id)}
                            title="Delete"
                            size="small"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                    {expandedRuleId === rule.id && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <strong>Blocks:</strong>
                          {(rule.blocks || []).map((block, blockIdx) => (
                            <div key={blockIdx}>
                              <strong>Block {blockIdx + 1}</strong>
                              <ul>
                                {block.conditions.map((cond, condIdx) => (
                                  <li key={`${blockIdx}-${condIdx}`}>
                                    {cond.field} {cond.operator} {cond.value}
                                  </li>
                                ))}
                              </ul>
                              <strong>Actions:</strong>
                              <ul>
                                {block.actions.map((act, actIdx) => (
                                  <li key={`${blockIdx}-${actIdx}`}>
                                    {act.value === 'set_card_highlight' && act.color
                                      ? `set_card_highlight - ${act.color.charAt(0).toUpperCase() + act.color.slice(1)}`
                                      : act.value}
                                  </li>
                                ))}
                              </ul>
                              {blockIdx < rule.blocks.length - 1 && (
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  my: 1,
                                  px: 2,
                                  py: 0.5,
                                  backgroundColor: '#f5f5f5',
                                  borderRadius: 1
                                }}>
                                  <Typography variant="body2" color="text.secondary">
                                    OR
                                  </Typography>
                                </Box>
                              )}
                            </div>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default RulesPage;